import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { useNavigate as useHistory } from 'react-router-dom'
import { useSnackbar } from 'notistack'
import CloseIcon from '@mui/icons-material/Close'
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined'
import { DialogActions, DialogTitle, Grid, TextField } from '@mui/material'

import { checkErrorResponse, Logger, LoggerTypes } from '../../../../utils'
import { createProject } from '../../../../actions'
import { Button, buttonModes, buttonTypes } from '../../../../components'
import { routePaths, getRoute } from '../../../../constants'
import './style.scss'

export const ProjectForm = ({ project, onCancel, onSubmit, errors }) => {
  const { name: defaultName, number: defaultNumber, description: defaultDescription } = project

  const [name, setName] = useState(defaultName)
  const [number, setNumber] = useState(defaultNumber)
  const [description, setDescription] = useState(defaultDescription)
  const [_errors, setErrors] = useState(errors)

  const dispatch = useDispatch()
  const { t } = useTranslation()
  const navigateTo = useHistory()
  const { enqueueSnackbar } = useSnackbar()

  const onChangeName = (e) => {
    const _value = e.target.value
    Logger.trace(LoggerTypes.CONTROLLER, 'ProjectForm.onChangeName()', { name: _value })
    setName(_value)
  }

  const onChangeNumber = (e) => {
    const _value = e.target.value
    Logger.trace(LoggerTypes.CONTROLLER, 'ProjectForm.onChangeNumber()', { number: _value })
    setNumber(_value)
  }

  const onChangeDescription = (e) => {
    const _value = e.target.value
    Logger.trace(LoggerTypes.CONTROLLER, 'ProjectForm.onChangeDescription()', { description: _value })
    setDescription(_value)
  }

  const onSubmitForm = (e) => {
    e.preventDefault()

    if (typeof onSubmit === 'function') {
      const updateProject = {
        ...project,
        name,
        number,
        description
      }
      return onSubmit(e, updateProject)
    }

    const newProject = {
      name,
      number,
      description
    }

    Logger.trace(LoggerTypes.CONTROLLER, 'ProjectForm.onSubmitForm()', { newProject })

    return dispatch(createProject(newProject))
      .then((res) => {
        const pid = res?.data?.id || 0
        Logger.trace(LoggerTypes.CONTROLLER, 'ProjectForm.onSubmitForm().createProject().then()', { pid, res })

        navigateTo(getRoute(routePaths.SCENARIO_LIST, { pid }))
      })
      .catch((res) => {
        Logger.trace(LoggerTypes.WARN, 'ProjectForm.onSubmitForm().createProject().catch()', { res })

        checkErrorResponse(res, enqueueSnackbar, t)

        if (res?.response?.data?.fields) {
          setErrors(res.response.data.fields)
        }
      })
  }

  const onCancelClick = (e) => {
    if (typeof onCancel === 'function') {
      return onCancel(e)
    }

    Logger.trace(LoggerTypes.CONTROLLER, 'ProjectForm.onCreateClick()')

    navigateTo(routePaths.PROJECT_LIST)

    return true
  }

  Logger.trace(LoggerTypes.CONTAINER, 'ProjectForm.render()', {
    name,
    defaultName,
    number,
    description,
    errors,
    _errors
  })

  return (
    <div className="ProjectForm">
      <DialogTitle id="form-dialog-title">{t('projects.about')}</DialogTitle>
      <form onSubmit={onSubmitForm}>
        <Grid container spacing={2} className="formContainer">
          <Grid item xs={12} sm={12}>
            <TextField
              type="text"
              name="name"
              id="name"
              style={{ width: '100%' }}
              value={name}
              label={t('projects.name')}
              onChange={onChangeName}
              placeholder={t('projects.name_placeholder')}
              error={Boolean(errors?.name || _errors?.name)}
              helperText={errors?.name || _errors?.name || ''}
            />
          </Grid>

          <Grid item xs={12} sm={12}>
            <TextField
              type="text"
              name="number"
              id="number"
              style={{ width: '100%' }}
              value={number}
              label={t('projects.number')}
              onChange={onChangeNumber}
              placeholder={t('projects.number_placeholder')}
              error={Boolean(errors?.number || _errors?.number)}
              helperText={errors?.number || _errors?.number || ''}
            />
          </Grid>

          <Grid item xs={12} sm={12}>
            <TextField
              type="text"
              name="description"
              id="description"
              style={{ width: '100%' }}
              value={description}
              multiline={true}
              rows={4}
              label={t('projects.description')}
              onChange={onChangeDescription}
              placeholder={t('projects.description_placeholder')}
              error={Boolean(errors?.description || _errors?.description)}
              helperText={errors?.description || _errors?.description || ''}
            />
          </Grid>
        </Grid>

        <DialogActions>
          <Button
            mode={buttonModes.SECONDARY_ACTION}
            type={buttonTypes.BUTTON}
            text={t('common.cancel')}
            icon={<CloseIcon />}
            onClick={onCancelClick}
          />
          <Button
            mode={buttonModes.PRIMARY_ACTION}
            type={buttonTypes.SUBMIT}
            text={t('common.save')}
            icon={<SaveOutlinedIcon />}
          />
        </DialogActions>
      </form>
    </div>
  )
}

ProjectForm.displayName = 'ProjectForm'

ProjectForm.propTypes = {
  project: PropTypes.object,
  onCancel: PropTypes.func,
  onSubmit: PropTypes.func,
  errors: PropTypes.any
}

ProjectForm.defaultProps = {
  project: {},
  onCancel: null,
  onSubmit: null,
  errors: null
}
